/**
 * Add Configs Dialog Component
 * Two-step dialog: 1) Select configs, 2) Set template values
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useGlobalConfig } from 'context/GlobalContext';
import { BglPrimarySmallButton, BglSecondarySmallButton } from 'components/button/BglButtons';
import { ConfigRegistryApis } from '../api/configRegistryAPI';
import { Template, ConfigRegistryItem } from '../types/configRegistryTypes';
import { ColumnHeader } from './ColumnHeader';

interface AddConfigsDialogProps {
  open: boolean;
  onClose: () => void;
  template: Template;
  onConfigsAdded: () => void;
}

const STEPS = ['Select Configs', 'Set Values'];

export const AddConfigsDialog = ({
  open,
  onClose,
  template,
  onConfigsAdded,
}: AddConfigsDialogProps) => {
  const { rc } = useGlobalConfig();

  // Lookup option type for filters
  interface LookupOption {
    documentId: string;
    displayName: string;
    code: string;
  }

  const [configs, setConfigs] = useState<ConfigRegistryItem[]>([]);
  const [configTypes, setConfigTypes] = useState<string[]>([]);
  const [components, setComponents] = useState<LookupOption[]>([]);
  const [classifications, setClassifications] = useState<LookupOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalConfigs, setTotalConfigs] = useState(0);

  // Step management
  const [activeStep, setActiveStep] = useState(0);

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConfigType, setSelectedConfigType] = useState<string>('');
  const [selectedComponent, setSelectedComponent] = useState<string>('');
  const [selectedClassification, setSelectedClassification] = useState<string>('');

  const [selectedConfigs, setSelectedConfigs] = useState<Set<string>>(new Set());
  // Store full config objects for selected configs to persist across pagination/filter changes
  const [selectedConfigsMap, setSelectedConfigsMap] = useState<Map<string, ConfigRegistryItem>>(new Map());
  // Track custom template values for each config (key: documentId, value: template value)
  const [customValues, setCustomValues] = useState<Record<string, string>>({});

  // Get existing config IDs in template
  const existingConfigIds = useMemo(() => {
    return new Set(
      template.items
        .map(item => item.configRegistryConfig?.documentId)
        .filter(Boolean) as string[]
    );
  }, [template.items]);

  // Fetch metadata (config types, components, classifications) on mount
  useEffect(() => {
    if (!open) return;

    const fetchMetadata = async () => {
      try {
        const [typesRes, schemaRes] = await Promise.all([
          ConfigRegistryApis.getConfigTypes(rc),
          ConfigRegistryApis.getUISchema(rc),
        ]);

        if (typesRes.data) {
          const types = (typesRes.data as any).data || typesRes.data;
          setConfigTypes(Array.isArray(types) ? types : []);
        }

        if (schemaRes.data) {
          const schemaData = schemaRes.data as any;
          const lookupTypes = schemaData.lookupTypes || schemaData.lookupFields || [];

          // Extract components (UPPERCASE code) - store full objects with documentId
          const componentLookup = lookupTypes.find((lt: any) => lt.code === 'COMPONENT');
          if (componentLookup) {
            const componentOptions = (componentLookup.lookups || componentLookup.options || [])
              .map((opt: any) => ({
                documentId: opt.documentId || opt.value,
                displayName: opt.displayName || opt.label || opt.code,
                code: opt.code,
              }))
              .filter((opt: LookupOption) => opt.documentId);
            setComponents(componentOptions);
          }

          // Extract categorizations (UPPERCASE code) - store full objects with documentId
          const categorizationLookup = lookupTypes.find((lt: any) => lt.code === 'CATEGORIZATION');
          if (categorizationLookup) {
            const classificationOptions = (categorizationLookup.lookups || categorizationLookup.options || [])
              .map((opt: any) => ({
                documentId: opt.documentId || opt.value,
                displayName: opt.displayName || opt.label || opt.code,
                code: opt.code,
              }))
              .filter((opt: LookupOption) => opt.documentId);
            setClassifications(classificationOptions);
          }
        }
      } catch (err: any) {
        console.error('Failed to fetch metadata:', err);
      }
    };

    fetchMetadata();
  }, [open, rc]);

  // Fetch configs with filters and pagination
  useEffect(() => {
    if (!open) return;

    const fetchConfigs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await ConfigRegistryApis.getConfigs(rc, {
          page,
          pageSize,
          configType: selectedConfigType || undefined,
          component: selectedComponent || undefined,
          classification: selectedClassification || undefined,
          search: searchQuery || undefined,
        });

        if (response.data) {
          setConfigs(response.data);
        }

        // Handle pagination metadata
        const meta = response.meta as {
          page?: number;
          pageSize?: number;
          pageCount?: number;
          total?: number;
        } | undefined;

        if (meta) {
          setTotalConfigs(meta.total || 0);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to fetch configs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchConfigs();
  }, [open, rc, page, pageSize, selectedConfigType, selectedComponent, selectedClassification, searchQuery]);

  // Filter out already added configs (server-side filtering handles the rest)
  const filteredConfigs = useMemo(() => {
    return configs.filter(config => !existingConfigIds.has(config.documentId));
  }, [configs, existingConfigIds]);

  // Calculate pagination
  const totalPages = Math.ceil(totalConfigs / pageSize);
  // Clamp availableCount at 0 to prevent negative values when existingConfigIds aren't scoped to current filters
  const availableCount = Math.max(0, totalConfigs - existingConfigIds.size);
  const currentPageStart = (page - 1) * pageSize + 1;
  const currentPageEnd = Math.min(page * pageSize, totalConfigs);

  const handleToggleConfig = (documentId: string) => {
    const newSelected = new Set(selectedConfigs);
    const newSelectedMap = new Map(selectedConfigsMap);
    if (newSelected.has(documentId)) {
      newSelected.delete(documentId);
      newSelectedMap.delete(documentId);
    } else {
      newSelected.add(documentId);
      // Store full config object for persistence across pagination/filter changes
      const config = configs.find(c => c.documentId === documentId);
      if (config) {
        newSelectedMap.set(documentId, config);
        // Initialize custom value with default value when selected
        // Use strict undefined check to preserve intentional empty strings
        if (customValues[documentId] === undefined) {
          setCustomValues(prev => ({
            ...prev,
            [documentId]: config.defaultValue || '',
          }));
        }
      }
    }
    setSelectedConfigs(newSelected);
    setSelectedConfigsMap(newSelectedMap);
  };

  const handleValueChange = (documentId: string, value: string) => {
    setCustomValues(prev => ({
      ...prev,
      [documentId]: value,
    }));
  };

  // Calculate current page selection state
  const selectedOnCurrentPage = filteredConfigs.filter(c => selectedConfigs.has(c.documentId)).length;
  const allCurrentPageSelected = filteredConfigs.length > 0 && selectedOnCurrentPage === filteredConfigs.length;
  const someCurrentPageSelected = selectedOnCurrentPage > 0 && selectedOnCurrentPage < filteredConfigs.length;

  const handleSelectAll = () => {
    const newSelected = new Set(selectedConfigs);
    const newSelectedMap = new Map(selectedConfigsMap);

    if (allCurrentPageSelected) {
      // Deselect all configs on current page only
      filteredConfigs.forEach(config => {
        newSelected.delete(config.documentId);
        newSelectedMap.delete(config.documentId);
      });
    } else {
      // Select all configs on current page only
      const newValues: Record<string, string> = { ...customValues };
      filteredConfigs.forEach(config => {
        newSelected.add(config.documentId);
        // Store full config object for persistence
        newSelectedMap.set(config.documentId, config);
        // Initialize custom values for newly selected configs
        // Use strict undefined check to preserve intentional empty strings
        if (newValues[config.documentId] === undefined) {
          newValues[config.documentId] = config.defaultValue || '';
        }
      });
      setCustomValues(newValues);
    }

    setSelectedConfigs(newSelected);
    setSelectedConfigsMap(newSelectedMap);
  };

  // Filter change handlers - reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleConfigTypeChange = (value: string) => {
    setSelectedConfigType(value);
    setPage(1);
  };

  const handleComponentChange = (value: string) => {
    setSelectedComponent(value);
    setPage(1);
  };

  const handleClassificationChange = (value: string) => {
    setSelectedClassification(value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedConfigType('');
    setSelectedComponent('');
    setSelectedClassification('');
    setPage(1);
  };

  // Get selected configs as array for step 2 - use stored map to persist across pages/filters
  const selectedConfigsList = useMemo(() => {
    return Array.from(selectedConfigsMap.values());
  }, [selectedConfigsMap]);

  const handleNext = () => {
    if (activeStep === 0 && selectedConfigs.size > 0) {
      // Initialize values for selected configs before moving to step 2
      const newValues: Record<string, string> = { ...customValues };
      selectedConfigsList.forEach(config => {
        if (newValues[config.documentId] === undefined) {
          newValues[config.documentId] = config.defaultValue || '';
        }
      });
      setCustomValues(newValues);
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setActiveStep(0);
  };

  const handleAddConfigs = async () => {
    if (selectedConfigs.size === 0) return;

    setIsSaving(true);
    try {
      // Build new items array with existing + new configs
      // Use Strapi v5 connect syntax for relations
      const existingItems = template.items.map(item => ({
        configRegistryConfig: item.configRegistryConfig?.documentId
          ? { connect: [{ documentId: item.configRegistryConfig.documentId }] }
          : undefined,
        value: item.value,
      })).filter(item => item.configRegistryConfig);

      // Add new configs with custom values (or default if not set)
      // Use selectedConfigsMap to get config data (persists across pagination/filter changes)
      const newItems = Array.from(selectedConfigs).map(docId => {
        const config = selectedConfigsMap.get(docId);
        // Use custom value if set, otherwise use default value
        const templateValue = customValues[docId] !== undefined
          ? customValues[docId]
          : (config?.defaultValue || null);
        return {
          configRegistryConfig: { connect: [{ documentId: docId }] },
          value: templateValue,
        };
      });

      await ConfigRegistryApis.updateTemplate(rc, template.documentId, {
        items: [...existingItems, ...newItems] as any,
      });

      onConfigsAdded();
      handleClose();
    } catch (err: any) {
      setError(err.message || 'Failed to add configs');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedConfigs(new Set());
    setSelectedConfigsMap(new Map());
    setCustomValues({});
    setSearchQuery('');
    setSelectedConfigType('');
    setActiveStep(0);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxWidth: '90vw',
          height: '90vh',
        }
      }}
    >
      <DialogTitle>
        Add Configs to Template
        <Typography variant="body2" color="text.secondary">
          {activeStep === 0 ? `Select configs to add to "${template.name}"` : `Set template values for selected configs`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Select Configs */}
        {activeStep === 0 && (
          <>
            {/* Search */}
            <Box sx={{ mb: 2 }}>
              <TextField
                placeholder="Search by config key or name..."
                size="small"
                fullWidth
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                <InputLabel>Config Type</InputLabel>
                <Select
                  value={selectedConfigType}
                  onChange={(e) => handleConfigTypeChange(e.target.value)}
                  label="Config Type"
                >
                  <MenuItem value="">All Types</MenuItem>
                  {configTypes.map(type => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                <InputLabel>Component</InputLabel>
                <Select
                  value={selectedComponent}
                  onChange={(e) => handleComponentChange(e.target.value)}
                  label="Component"
                >
                  <MenuItem value="">All Components</MenuItem>
                  {components.map(comp => (
                    <MenuItem key={comp.documentId} value={comp.documentId}>{comp.displayName}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 150, flex: 1 }}>
                <InputLabel>Classification</InputLabel>
                <Select
                  value={selectedClassification}
                  onChange={(e) => handleClassificationChange(e.target.value)}
                  label="Classification"
                >
                  <MenuItem value="">All Classifications</MenuItem>
                  {classifications.map(cls => (
                    <MenuItem key={cls.documentId} value={cls.documentId}>{cls.displayName}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <BglSecondarySmallButton
                onClick={handleClearFilters}
                sx={{ minWidth: 120 }}
              >
                Clear Filters
              </BglSecondarySmallButton>
            </Box>

            {/* Stats */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages} • {currentPageStart}-{currentPageEnd} of {totalConfigs} total configs • {availableCount} available to add
              </Typography>
              <Chip
                label={`${selectedConfigs.size} selected`}
                color={selectedConfigs.size > 0 ? 'primary' : 'default'}
                size="small"
              />
            </Box>

            {/* Configs Table */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={someCurrentPageSelected}
                          checked={allCurrentPageSelected}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Config Key" tooltipKey="configKey" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Display Name" tooltipKey="displayName" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Config Type" tooltipKey="configType" />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        <ColumnHeader label="Default Value" tooltipKey="defaultValue" />
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredConfigs.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            No configs available to add
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredConfigs.map(config => {
                        const isSelected = selectedConfigs.has(config.documentId);
                        const isJSON = config.configDataType === 'JSON';
                        const defaultValueStr = config.defaultValue || '-';

                        return (
                          <TableRow
                            key={config.documentId}
                            hover
                            selected={isSelected}
                            onClick={() => handleToggleConfig(config.documentId)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox checked={isSelected} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                {config.configKey}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                {config.displayName}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={config.configType} size="small" variant="outlined" />
                            </TableCell>
                            <TableCell>
                              <Box
                                sx={{
                                  maxWidth: 300,
                                  maxHeight: 60,
                                  overflow: 'auto',
                                  p: 0.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    fontFamily: isJSON ? 'monospace' : 'inherit',
                                    fontSize: '0.8rem',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                  }}
                                >
                                  {defaultValueStr}
                                </Typography>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Pagination Controls */}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <BglSecondarySmallButton
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                >
                  Previous
                </BglSecondarySmallButton>
                <Typography variant="body2" color="text.secondary">
                  Page {page} of {totalPages || 1}
                </Typography>
                <BglSecondarySmallButton
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || isLoading || totalPages === 0}
                >
                  Next
                </BglSecondarySmallButton>
              </Box>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel>Per Page</InputLabel>
                <Select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  label="Per Page"
                >
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </>
        )}

        {/* Step 2: Set Values */}
        {activeStep === 1 && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set template values for the {selectedConfigsList.length} selected config(s).
              Leave blank to use the default value.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                      <ColumnHeader label="Config Key" tooltipKey="configKey" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '15%' }}>
                      <ColumnHeader label="Display Name" tooltipKey="displayName" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '10%' }}>
                      <ColumnHeader label="Config Type" tooltipKey="configType" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '25%' }}>
                      <ColumnHeader label="Default Value" tooltipKey="defaultValue" />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, width: '35%' }}>
                      <ColumnHeader label="Template Value" tooltipKey="templateValue" />
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedConfigsList.map(config => {
                    const isJSON = config.configDataType === 'JSON';
                    const defaultValueStr = config.defaultValue || '-';

                    return (
                      <TableRow key={config.documentId} sx={{ '& > *': { verticalAlign: 'top !important' } }}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                            {config.configKey}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                            {config.displayName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={config.configType} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              maxHeight: 120,
                              maxWidth: 300,
                              overflow: 'auto',
                              p: 1,
                              bgcolor: 'grey.100',
                              borderRadius: 1,
                              border: '1px solid',
                              borderColor: 'grey.300',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: isJSON ? 'monospace' : 'inherit',
                                fontSize: '0.8rem',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'text.disabled',
                                fontStyle: 'italic',
                              }}
                            >
                              {defaultValueStr}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={6}
                            placeholder={config.defaultValue || 'Enter value...'}
                            value={customValues[config.documentId] ?? ''}
                            onChange={(e) => handleValueChange(config.documentId, e.target.value)}
                            sx={{
                              '& .MuiInputBase-root': {
                                bgcolor: 'background.paper',
                              },
                              '& .MuiInputBase-input': {
                                fontFamily: isJSON ? 'monospace' : 'inherit',
                                fontSize: '0.85rem',
                                color: 'text.primary',
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <BglSecondarySmallButton onClick={handleClose} disabled={isSaving}>
          Cancel
        </BglSecondarySmallButton>

        {activeStep === 0 ? (
          <BglPrimarySmallButton
            onClick={handleNext}
            disabled={selectedConfigs.size === 0}
            endIcon={<ArrowForwardIcon />}
          >
            Next: Set Values
          </BglPrimarySmallButton>
        ) : (
          <>
            <BglSecondarySmallButton onClick={handleBack} disabled={isSaving} startIcon={<ArrowBackIcon />}>
              Back
            </BglSecondarySmallButton>
            <BglPrimarySmallButton
              onClick={handleAddConfigs}
              disabled={isSaving}
            >
              {isSaving ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
              Add {selectedConfigs.size} Config{selectedConfigs.size > 1 ? 's' : ''}
            </BglPrimarySmallButton>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddConfigsDialog;

